import "./styles/index.css";

import React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app/App";
import { initFaro } from "./observability/faro";
import { webCapabilities } from "./platform/capabilities";
import {
  loadRuntimeConfig,
  getRuntimeConfig,
} from "@shared-core/config/runtime-config";
import { linkTokenFromFragment } from "@shared-core/domain/supplier-link-fragment";

// INVARIANT: nothing in this file's *static* import graph may read
// configuration — see apps/frontend/src/main.tsx (frontend-architecture.md's
// "one environment-agnostic artifact").
//
// Mode selection, as the customer portal's: no API URL configured means mock
// mode (local dev, Storybook, the Playwright build): every page, on mock data.
// With an API URL (the supplier app at /supplier-api,
// docs/features/supplier-interface/02_enterprise_architecture_impact_assessment.md
// §5.9) every page runs on the real adapters: Entra sign-in, the purchase
// orders and invoices (by link or login), and the warehouse picks and goods in.

const container = document.getElementById("root")!;
const root = createRoot(container);

async function boot() {
  await loadRuntimeConfig();

  // An emailed link's token is in the URL fragment. Faro records page URLs,
  // so it stays off for a session that starts on a link.
  if (linkTokenFromFragment(window.location.hash) === null) {
    try {
      initFaro();
    } catch (err) {
      console.error("[faro] init failed:", err);
    }
  }

  // Staff-view mode: a build-time constant (VITE_PORTAL_MODE=staff_view, set only by the staff-view build stage:
  // docker/Dockerfile.frontend and apps/supplier-portal/vite.config.ts), read only here inside boot(), never at
  // module scope, so the supplier build folds it to false and drops this branch with every chunk behind it (review
  // M5, docs/features/supplier-portal-view-as/05 §6.1). Real staff auth (MSAL) only runs when a real API base URL is
  // configured; otherwise this falls through to the mock staff adapters, so Storybook, Playwright and local review
  // need no live Entra tenant. The supplier HTTP adapters and the supplier mocks are never imported on this path.
  if (import.meta.env?.VITE_PORTAL_MODE === "staff_view") {
    // Dynamic, like everything else of the staff view: the path it parses names the staff view's base.
    const { parseStaffViewSupplier } = await import("./staff-view/target");
    const target = parseStaffViewSupplier(window.location.pathname);
    if (target === null) {
      const { StaffViewNoSupplier } =
        await import("./staff-view/StaffViewNoSupplier");
      root.render(
        <React.StrictMode>
          <StaffViewNoSupplier />
        </React.StrictMode>,
      );
      return;
    }
    const hasApiUrl = Boolean(getRuntimeConfig().apiBaseUrl);
    // Against a local API running AUTH_MODE=dev_bypass there is no Entra to sign in to and the API takes the request
    // without a token, as the staff app's own dev_bypass mode does.
    const isDevBypass = getRuntimeConfig().authMode === "dev_bypass";
    if (hasApiUrl && !isDevBypass) {
      const { bootStaffAuth } = await import("./staff-view/staffAuth");
      const authResult = await bootStaffAuth();
      if (!authResult.ok) {
        container.replaceChildren();
        const message = document.createElement("p");
        message.textContent = authResult.message;
        message.style.cssText =
          "font-family: system-ui, sans-serif; padding: 2rem; color: #7c2d12;";
        container.appendChild(message);
        return;
      }
    }
    const { StaffViewApp } = await import("./staff-view/StaffViewApp");
    // Leaving is a full navigation into One, never a route change inside this bundle.
    const { staffViewExitPath } = await import("./staff-view/exit");
    const exitToOne = (supplierNo: string | null) =>
      webCapabilities.redirectTo(staffViewExitPath(supplierNo));
    const adapters = hasApiUrl
      ? (
          await import("@shared-core/data/api/staff-view-supplier-adapter")
        ).createStaffViewSupplierAdapters({
          supplierId: target.supplierId,
          exitToOne,
        })
      : (
          await import("@shared-core/mock-data/staff-view-supplier-mock-adapter")
        ).createMockStaffViewSupplierAdapters({
          supplierId: target.supplierId,
          exitToOne,
        });
    root.render(
      <React.StrictMode>
        <StaffViewApp adapters={adapters} basename={target.basename} />
      </React.StrictMode>,
    );
    return;
  }

  if (getRuntimeConfig().apiBaseUrl) {
    const {
      createHttpSupplierGoodsInAdapter,
      createHttpSupplierPicksAdapter,
      createHttpSupplierPortalAdapter,
      createHttpSupplierPortalSignIn,
    } = await import("@shared-core/data/api/supplier-portal");
    root.render(
      <React.StrictMode>
        <App
          data={createHttpSupplierPortalAdapter()}
          signIn={createHttpSupplierPortalSignIn({
            redirectTo: webCapabilities.redirectTo,
          })}
          picks={createHttpSupplierPicksAdapter()}
          goodsIn={createHttpSupplierGoodsInAdapter()}
        />
      </React.StrictMode>,
    );
    return;
  }

  const { mockSupplierPortalAdapter, mockSupplierPortalSignIn } =
    await import("@shared-core/mock-data/supplier-portal");
  const { mockSupplierPicksAdapter } =
    await import("@shared-core/mock-data/supplier-picks");
  const { mockSupplierGoodsInAdapter } =
    await import("@shared-core/mock-data/supplier-goods-in");
  root.render(
    <React.StrictMode>
      <App
        data={mockSupplierPortalAdapter}
        signIn={mockSupplierPortalSignIn}
        picks={mockSupplierPicksAdapter}
        goodsIn={mockSupplierGoodsInAdapter}
      />
    </React.StrictMode>,
  );
}

boot().catch((err) => {
  console.error("[boot] application bootstrap failed:", err);
});
